import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { Check, DoorOpen, PenLine, RotateCcw, Trash2 } from "lucide-react";
import { countWallReview } from "../../countWallReview";
import type { KarlWallsReviewProps } from "./types";

const ANSWER_CLASS =
  "flex min-h-[48px] min-w-0 flex-1 basis-0 items-center justify-center gap-1.5 rounded-md px-3 py-2 text-center text-[16px] leading-snug transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";
const APPROVE_CLASS = "bg-blue-600 font-semibold text-white hover:opacity-90";
const REJECT_CLASS =
  "karl-hover-dwell border border-slate-300 bg-white font-medium text-slate-800 hover:bg-slate-100";
const TOOL_CLASS =
  "karl-hover-dwell flex min-w-0 flex-1 basis-0 items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2 py-2 text-[13.5px] font-medium text-slate-700 hover:border-blue-400 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-50";

export const KarlWallsReview = ({ control, onConfirm }: KarlWallsReviewProps) => {
  const { t } = useTranslation();
  const {
    walls,
    summary,
    didSaveFail,
    isCorrecting,
    setIsCorrecting,
    selectedWallId,
    removeWall,
    toggleWallKind,
    resetToDetected,
    draft,
    onDraw,
    onFinishDraw,
    onCancelDraw,
  } = control;

  const counts = useMemo(() => countWallReview({ walls, summary }), [walls, summary]);
  const selected = walls.find((wall) => wall.id === selectedWallId) ?? null;

  const facts = [
    { key: "wallsFactWalls", value: counts.walls },
    { key: "wallsFactDoors", value: counts.doors },
    ...(counts.changed > 0 ? [{ key: "wallsFactChanged", value: counts.changed }] : []),
  ];

  const saveFailed = didSaveFail && (
    <p className="mt-2 rounded-md border border-red-300 bg-red-50 px-2 py-1.5 text-[13px] font-medium text-red-800">
      {t("smartSurveyor.cablesBeta.wallsNotSaved")}
    </p>
  );

  if (!isCorrecting) {
    return (
      <div
        data-testid="karl-walls-review"
        className="karl-rise karl-await rounded-xl border border-blue-200 bg-blue-50/60 px-3 py-3"
      >
        <div className="text-[17px] font-semibold leading-[1.45] text-slate-800">
          {t("smartSurveyor.cablesBeta.askWallsMatch")}
        </div>
        <div className="mt-1 text-[14px] leading-[1.5] text-slate-600">
          {t("smartSurveyor.cablesBeta.askWallsMatchDetail")}
        </div>
        <dl className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[14px] text-slate-600">
          {facts.map((fact) => (
            <div key={fact.key} className="flex gap-1.5">
              <dt>{t(`smartSurveyor.cablesBeta.${fact.key}`)}</dt>
              <dd className="font-semibold tabular-nums text-slate-900">{fact.value}</dd>
            </div>
          ))}
        </dl>
        <div className="mt-3 flex items-stretch gap-3">
          <button
            type="button"
            onClick={onConfirm}
            className={clsx(ANSWER_CLASS, APPROVE_CLASS)}
          >
            <Check size={18} strokeWidth={2.75} className="flex-none" />
            <span className="min-w-0">{t("smartSurveyor.cablesBeta.answerWallsMatch")}</span>
          </button>
          <button
            type="button"
            onClick={() => setIsCorrecting(true)}
            className={clsx(ANSWER_CLASS, REJECT_CLASS)}
          >
            <span className="min-w-0">{t("smartSurveyor.cablesBeta.answerWallsCorrect")}</span>
          </button>
        </div>
        {saveFailed}
      </div>
    );
  }

  return (
    <div
      data-testid="karl-walls-correcting"
      className="karl-rise rounded-xl border border-blue-200 bg-blue-50/60 px-3 py-3"
    >
      <div className="text-[17px] font-semibold leading-[1.45] text-slate-800">
        {t("smartSurveyor.cablesBeta.wallsCorrectTitle")}
      </div>

      {draft ? (
        // Nothing is saved until he finishes, so the corner count is what says the plan is listening.
        <div className="mt-2 rounded-lg border border-blue-300 bg-white p-2">
          <div className="text-[14px] font-medium text-blue-800">
            {t(
              draft.wallKind === "opening"
                ? "smartSurveyor.cablesBeta.wallsDrawingOpening"
                : "smartSurveyor.cablesBeta.wallsDrawing",
              { count: draft.points.length },
            )}
          </div>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              disabled={draft.points.length < 2}
              onClick={onFinishDraw}
              className={clsx(ANSWER_CLASS, APPROVE_CLASS, "min-h-[40px] text-[14px]")}
            >
              <Check size={15} />
              {t("smartSurveyor.cablesBeta.wallsFinish")}
            </button>
            <button
              type="button"
              onClick={onCancelDraw}
              className={clsx(ANSWER_CLASS, REJECT_CLASS, "min-h-[40px] text-[14px]")}
            >
              {t("smartSurveyor.cablesBeta.cancel")}
            </button>
          </div>
        </div>
      ) : selected ? (
        <div data-testid="karl-wall-selected" className="mt-2 rounded-lg border border-blue-300 bg-white p-2">
          <div className="text-[14px] font-medium text-slate-800">
            {t(
              selected.kind === "opening"
                ? "smartSurveyor.cablesBeta.wallsSelectedDoor"
                : "smartSurveyor.cablesBeta.wallsSelectedWall",
            )}
          </div>
          <div className="mt-2 flex gap-2">
            <button type="button" onClick={() => removeWall(selected.id)} className={TOOL_CLASS}>
              <Trash2 size={15} />
              {t("smartSurveyor.cablesBeta.wallsRemoveSelected")}
            </button>
            <button type="button" onClick={() => toggleWallKind(selected.id)} className={TOOL_CLASS}>
              {selected.kind === "wall" && <DoorOpen size={15} />}
              {t(
                selected.kind === "wall"
                  ? "smartSurveyor.cablesBeta.wallsMakeDoor"
                  : "smartSurveyor.cablesBeta.wallsMakeWall",
              )}
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-1 text-[14px] leading-[1.5] text-slate-600">
          {t("smartSurveyor.cablesBeta.wallsCorrectHint")}
        </div>
      )}

      {!draft && (
        <div className="mt-2 flex gap-2">
          <button type="button" onClick={() => onDraw("wall")} className={TOOL_CLASS}>
            <PenLine size={15} />
            {t("smartSurveyor.cablesBeta.wallsDraw")}
          </button>
          <button type="button" onClick={() => onDraw("opening")} className={TOOL_CLASS}>
            <DoorOpen size={15} />
            {t("smartSurveyor.cablesBeta.wallsDrawOpening")}
          </button>
        </div>
      )}

      {saveFailed}

      <div className="mt-3 flex items-center gap-2 border-t border-slate-300 pt-2">
        <button
          type="button"
          disabled={Boolean(draft) || counts.changed === 0}
          onClick={resetToDetected}
          className="karl-hover-dwell flex items-center gap-1 rounded-md px-2 py-2 text-[14px] text-slate-600 hover:bg-slate-100 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <RotateCcw size={14} />
          {t("smartSurveyor.cablesBeta.wallsResetToDetected")}
        </button>
        <button
          type="button"
          disabled={Boolean(draft)}
          onClick={() => setIsCorrecting(false)}
          className="ml-auto rounded-md bg-blue-600 px-4 py-2 text-[14px] font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {t("smartSurveyor.cablesBeta.wallsCorrectDone")}
        </button>
      </div>
    </div>
  );
};
